import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useAuth } from '@/lib/auth-context';
import { useGoogleAuth } from '@/hooks/useGoogleAuth';
import { RecostLogo } from '@/components/RecostLogo';
import { Spinner } from '@/components/ui/spinner';
import { colors } from '@/lib/tokens';

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" />
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" />
    </svg>
  );
}

export default function Login() {
  const { isAuthenticated, isLoading, login: applyToken } = useAuth();
  const navigate = useNavigate();
  const [authError, setAuthError] = useState<string | null>(null);

  // Single navigation path: fires for warm sessions on mount AND once login
  // populates the user (isAuthenticated flips true).
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      const redirect = sessionStorage.getItem('ecoapi_redirect') ?? '/dashboard';
      sessionStorage.removeItem('ecoapi_redirect');
      navigate(redirect, { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  const { login } = useGoogleAuth(
    (token) => { applyToken(token); },
    (msg) => setAuthError(msg),
  );

  if (isLoading || isAuthenticated) {
    return (
      <div className="flex h-screen items-center justify-center" style={{ backgroundColor: colors.bgPage }}>
        <Spinner />
      </div>
    );
  }

  return (
    <main
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.bgPage,
        padding: 24,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 400,
          background: colors.bgBase,
          border: `1px solid ${colors.borderDefault}`,
          borderRadius: 6,
          padding: '40px 32px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 28 }}>
          <RecostLogo size={30} />
        </div>

        <h1
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 22,
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: colors.textPrimary,
            textAlign: 'center',
            margin: 0,
          }}
        >
          Sign in to Recost
        </h1>
        <p
          style={{
            fontSize: 14,
            color: colors.textSecondary,
            textAlign: 'center',
            margin: '8px 0 28px',
            lineHeight: 1.5,
          }}
        >
          Know exactly what your APIs cost.
        </p>

        <button
          onClick={login}
          style={{
            width: '100%',
            height: 44,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            background: colors.bgBase,
            border: `1px solid ${colors.borderDefault}`,
            borderRadius: 6,
            fontSize: 14,
            fontWeight: 600,
            color: colors.textPrimary,
            cursor: 'pointer',
            transition: 'background 0.15s, border-color 0.15s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = colors.bgSubtle;
            e.currentTarget.style.borderColor = colors.borderHover;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = colors.bgBase;
            e.currentTarget.style.borderColor = colors.borderDefault;
          }}
        >
          <GoogleIcon />
          Continue with Google
        </button>

        {authError && (
          <p style={{ fontSize: 13, color: colors.error, textAlign: 'center', margin: '16px 0 0' }}>
            {authError}
          </p>
        )}

        <p
          style={{
            fontSize: 12,
            color: colors.textMuted,
            textAlign: 'center',
            margin: '24px 0 0',
            lineHeight: 1.5,
          }}
        >
          By continuing you agree to our{' '}
          <Link to="/terms" style={{ color: colors.textSecondary, textDecoration: 'underline' }}>Terms</Link>
          {' '}and{' '}
          <Link to="/privacy" style={{ color: colors.textSecondary, textDecoration: 'underline' }}>Privacy Policy</Link>.
        </p>
      </div>
    </main>
  );
}
